import { z } from 'zod'
import { type EntityId, AccountStatusIdSchema, CompanyIdSchema, idSchemaV1, TeamIdSchema, UserIdSchema } from '../ids'
import { EventDateTypeSchema, SitOutcomeFaultSchema } from '../eventOutcome'
import { AuditPropsSchema } from '../common'
import { ArchivableSchema } from '../archive'

export const LEGACY_DISPOSITIONS = [
  'NotHome',
  'Comeback',
  'NotNow',
  'NotQualified',
  'PartialPitch',
  'NewAccount',
  'Competitor',
  'Custom1',
  'Custom2',
  'Custom3',
] as const

/**
 * @deprecated use DispositionId instead
 */
export const DispositionCodeSchema = z.enum(LEGACY_DISPOSITIONS)
export type DispositionCode = z.infer<typeof DispositionCodeSchema>

export const DispositionIdSchema = z
  .union([idSchemaV1('Disposition'), DispositionCodeSchema])
  .meta({ deprecated: true, description: 'Deprecated when using Workflows' })
export type DispositionId = z.infer<typeof DispositionIdSchema>

export const withDispositionIdSchema = z.object({
  dispositionId: DispositionIdSchema,
})
export type WithDispositionId = z.infer<typeof withDispositionIdSchema>

export type DispositionScopeId = EntityId

const LegacyDispositionSchema = z.object({
  scope: z.literal('legacy'),
})
type LegacyDisposition = z.infer<typeof LegacyDispositionSchema>

const CompanyDispositionSchema = z.object({
  scope: z.literal('company'),
  scopeId: CompanyIdSchema,
})
type CompanyDisposition = z.infer<typeof CompanyDispositionSchema>

const TeamDispositionSchema = z.object({
  scope: z.literal('team'),
  scopeId: TeamIdSchema,
})
type TeamDisposition = z.infer<typeof TeamDispositionSchema>

const UserDispositionSchema = z.object({
  scope: z.literal('user'),
  scopeId: UserIdSchema,
})
type UserDisposition = z.infer<typeof UserDispositionSchema>

export type DispositionScope = UserDisposition | TeamDisposition | CompanyDisposition | LegacyDisposition

export const LEGACY_ICONS = [
  'plus',
  'door-closed',
  'clockcircle',
  'hour-glass',
  'not-interested',
  'flag',
  'comment-dots',
  'home-floor-1',
  'home-floor-2',
  'home-floor-3',
]
export const LegacyIconSchema = z.enum(LEGACY_ICONS)
export type LegacyIcon = z.infer<typeof LegacyIconSchema>
export const isLegacyIcon = (icon: unknown): icon is LegacyIcon => LegacyIconSchema.safeParse(icon).success

const DispositionIconSchema = z.union([LegacyIconSchema, z.string()])

export const UnsavedDispositionBaseSchema = z.object({
  name: z.string().nonempty().meta({
    id: 'dispositionName',
    description: 'The name of the disposition',
  }),
  order: z.number().meta({
    id: 'dispositionOrder',
    description: 'The position of the disposition',
  }),
  color: z.string().meta({
    id: 'dispositionColor',
    description: 'The color of the disposition',
  }),
  code: z.string().optional().meta({
    id: 'dispositionCode',
    description: "The disposition's shorthand code",
  }),
  interaction: z.boolean().optional().meta({
    id: 'dispositionInteraction',
    description: 'Indicates if the disposition involves user interaction',
  }),
  pitch: z.boolean().optional().meta({
    id: 'dispositionPitch',
    description: 'Indicates if the disposition is a pitch',
  }),
  icon: DispositionIconSchema,
  createAccount: z.boolean().optional().meta({
    id: 'dispositionCreateAccount',
  }),
  accountStatusId: AccountStatusIdSchema.optional().meta({
    id: 'dispositionAccountStatusId',
  }),
  fromStatusId: AccountStatusIdSchema.optional().meta({
    id: 'dispositionFromStatusId',
  }),
  showIfNoStatusId: z.boolean().optional().meta({
    id: 'dispositionShowIfNoStatusId',
  }),
  scheduleAppointment: z.boolean().meta({
    id: 'dispositionScheduleAppointment',
  }),
  occurred: z.boolean().optional().meta({
    id: 'dispositionOccurred',
  }),
  closed: z.boolean().optional().meta({
    id: 'dispositionClosed',
  }),
  fault: SitOutcomeFaultSchema.optional().meta({
    id: 'dispositionFault',
  }),
  parentOnly: z.boolean().optional().meta({
    id: 'dispositionParentOnly',
    description: 'Indicates if the disposition is for parent accounts',
  }),
  eventDateType: EventDateTypeSchema.optional().meta({
    id: 'dispositionEventDateType',
  }),
})
export const UnsavedDispositionSchema = z.union([
  z.object({ ...LegacyDispositionSchema.shape, ...UnsavedDispositionBaseSchema.shape }),
  z.object({ ...CompanyDispositionSchema.shape, ...UnsavedDispositionBaseSchema.shape }),
  z.object({ ...TeamDispositionSchema.shape, ...UnsavedDispositionBaseSchema.shape }),
  z.object({ ...UserDispositionSchema.shape, ...UnsavedDispositionBaseSchema.shape }),
])
export type UnsavedDisposition = z.infer<typeof UnsavedDispositionSchema>

export const isDispositionId = (val: unknown): val is DispositionId => DispositionIdSchema.safeParse(val).success

const DispositionDataBaseSchema = z.object({
  ...ArchivableSchema.shape,
  ...AuditPropsSchema.shape,
  ...UnsavedDispositionBaseSchema.shape,
  ...withDispositionIdSchema.shape,
})

export const DispositionDataSchema = z.union([
  z.object({ ...LegacyDispositionSchema.shape, ...DispositionDataBaseSchema.shape }),
  z.object({ ...CompanyDispositionSchema.shape, ...DispositionDataBaseSchema.shape }),
  z.object({ ...TeamDispositionSchema.shape, ...DispositionDataBaseSchema.shape }),
  z.object({ ...UserDispositionSchema.shape, ...DispositionDataBaseSchema.shape }),
])
export type DispositionData = z.infer<typeof DispositionDataSchema>
